import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

const LEVEL_ICON = { ok: '✅', warn: '⚠️', block: '⛔' };
const LEVEL_COLOR = { ok: '#00ff88', warn: '#f59e0b', block: '#ff4444' };

function Assistant() {
  const [health, setHealth] = useState(null);
  const [messages, setMessages] = useState([
    { who: 'ai', text: "I'm your trading assistant. I know everything happening inside this app — ask me why it's not trading, what today's P&L is, what's blocked, or how to fix any error." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  // ── Instant per-symbol recommendation (stock / option / commodity / cmdty option) ──
  const [recSym, setRecSym] = useState('');
  const [recSug, setRecSug] = useState([]);
  const [recRes, setRecRes] = useState(null);
  const [recBusy, setRecBusy] = useState(false);
  const recTimer = useRef(null);

  const onRecType = (v) => {
    setRecSym(v); setRecRes(null);
    if (recTimer.current) clearTimeout(recTimer.current);
    if (!v || v.trim().length < 1) { setRecSug([]); return; }
    recTimer.current = setTimeout(async () => {
      try { const r = await api.searchSymbols(v.trim(), '', '', 8); setRecSug((r.symbols || r.results || []).slice(0, 8)); }
      catch (e) { setRecSug([]); }
    }, 180);
  };

  const getRec = async (sym) => {
    const s = (sym || recSym).trim();
    if (!s) return;
    setRecSym(s); setRecSug([]); setRecBusy(true); setRecRes(null);
    try { setRecRes(await api.assistantRecommend(s)); }
    catch (e) { setRecRes({ status: 'error', message: 'Could not fetch recommendation' }); }
    setRecBusy(false);
  };

  const loadHealth = useCallback(async () => {
    try { setHealth(await api.assistantHealth()); } catch (e) {}
  }, []);

  useEffect(() => {
    loadHealth();
    const iv = setInterval(loadHealth, 10000);
    return () => clearInterval(iv);
  }, [loadHealth]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const ask = async (q) => {
    const question = (q || input).trim();
    if (!question || busy) return;
    setMessages(m => [...m, { who: 'me', text: question }]);
    setInput('');
    setBusy(true);
    try {
      const res = await api.assistantAsk(question);
      setMessages(m => [...m, { who: 'ai', text: res.answer || 'No answer.' }]);
      if (res.health) setHealth(res.health);
    } catch (e) {
      setMessages(m => [...m, { who: 'ai', text: 'Could not reach the engine — is the backend running?' }]);
    }
    setBusy(false);
  };

  const quick = ['Why not trading?', 'Check everything', "Today's profit", 'Open positions', 'Errors', 'Fyers status'];

  return (
    <div style={{ padding: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Instant recommendation — type any symbol, get buy/sell + sentiment */}
      <div className="card" style={{ flex: '1 1 100%' }}>
        <div className="card-header">
          <span className="card-title">📈 Instant Recommendation</span>
          <span style={{ color: '#666', fontSize: '0.7rem' }}>stock · option · commodity · commodity option — buy/sell price + sentiment</span>
        </div>
        <div style={{ position: 'relative', maxWidth: '560px' }}>
          <input value={recSym} onChange={e => onRecType(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') getRec(); }}
            placeholder="Type a symbol — RELIANCE, NIFTY 24500 CE, CRUDEOIL, GOLD, SILVERM 90000 PE…"
            style={{ width: '100%', padding: '0.55rem', background: '#1a1a2e', color: '#fff', border: '1px solid #444', borderRadius: '6px' }} />
          {recSug.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 30, left: 0, right: 0, background: '#12121e', border: '1px solid #333', borderRadius: '6px', marginTop: '2px', maxHeight: '260px', overflowY: 'auto' }}>
              {recSug.map((s, i) => (
                <div key={i} onMouseDown={e => e.preventDefault()} onClick={() => getRec(s.tradingsymbol)}
                  style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem', borderBottom: '1px solid #222', display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                  <span style={{ color: '#ddd' }}>{s.tradingsymbol}</span>
                  <span style={{ color: s.exchange === 'MCX' ? '#ffb020' : '#00d9ff', fontSize: '0.68rem', whiteSpace: 'nowrap' }}>{s.kind} · {s.exchange}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="btn btn-start" style={{ marginTop: '0.5rem' }} onClick={() => getRec()} disabled={recBusy}>Get Recommendation</button>
        {recBusy && <p style={{ color: '#666', fontSize: '0.8rem', margin: '0.4rem 0 0' }}>analyzing…</p>}
        {recRes && recRes.status === 'success' && (
          <div style={{ marginTop: '0.7rem', border: '1px solid #333', borderRadius: '8px', padding: '0.7rem', background: '#111' }}>
            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ color: '#eee', fontSize: '1rem' }}>{recRes.symbol}</strong>
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: recRes.signal === 'BUY' ? '#00ff88' : recRes.signal === 'SELL' ? '#ff5555' : '#888' }}>{recRes.signal}</span>
              <span style={{ color: recRes.sentiment === 'Bullish' ? '#00ff88' : recRes.sentiment === 'Bearish' ? '#ff5555' : '#f59e0b' }}>Sentiment: {recRes.sentiment}</span>
              <span style={{ color: '#aaa', fontSize: '0.8rem' }}>Score {recRes.score} · Conf {recRes.confidence}% · {recRes.trend}</span>
            </div>
            <div style={{ color: '#00d9ff', marginTop: '0.45rem', fontSize: '0.88rem', fontWeight: 600 }}>{recRes.action}</div>
            {recRes.price > 0 && (recRes.signal === 'BUY' || recRes.signal === 'SELL') && (
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                <span style={{ color: '#ccc', fontSize: '0.82rem' }}>{recRes.signal === 'SELL' ? 'Sell' : 'Buy'} <b style={{ color: '#fff' }}>{recRes.buy_price ?? recRes.price}</b></span>
                <span style={{ color: '#00ff88', fontSize: '0.82rem' }}>Target <b>{recRes.target || '—'}</b></span>
                <span style={{ color: '#ff8888', fontSize: '0.82rem' }}>Stop <b>{recRes.stop || '—'}</b></span>
                <span style={{ color: '#ffb020', fontSize: '0.82rem' }}>⏱ Time to target <b>{recRes.target_eta || '—'}</b></span>
              </div>
            )}
            <div style={{ color: recRes.would_trade ? '#00ff88' : '#f59e0b', marginTop: '0.35rem', fontSize: '0.75rem' }}>{recRes.note}</div>
            {recRes.reasons?.length > 0 && <div style={{ color: '#888', marginTop: '0.3rem', fontSize: '0.72rem' }}>Why: {recRes.reasons.join(' · ')}</div>}
          </div>
        )}
        {recRes && recRes.status === 'error' && <p style={{ color: '#ff5555', marginTop: '0.5rem', fontSize: '0.8rem' }}>{recRes.message}</p>}
      </div>

      {/* Chat */}
      <div className="card" style={{ flex: '2 1 480px', display: 'flex', flexDirection: 'column', minHeight: '540px' }}>
        <div className="card-header">
          <span className="card-title">🤖 Trading Assistant</span>
          <span style={{ color: '#666', fontSize: '0.7rem' }}>knows only this trading app — live data, real answers</span>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: '420px', padding: '0.4rem 0' }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.who === 'me' ? 'flex-end' : 'flex-start', margin: '0.35rem 0' }}>
              <div style={{
                maxWidth: '85%', whiteSpace: 'pre-wrap', fontSize: '0.82rem', lineHeight: 1.5,
                background: m.who === 'me' ? '#00d9ff22' : '#1a1a2e',
                border: `1px solid ${m.who === 'me' ? '#00d9ff55' : '#333'}`,
                borderRadius: '10px', padding: '0.5rem 0.7rem', color: '#ddd',
              }}>{m.text}</div>
            </div>
          ))}
          {busy && <div style={{ color: '#666', fontSize: '0.75rem' }}>thinking…</div>}
          <div ref={bottomRef} />
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', margin: '0.4rem 0' }}>
          {quick.map(qk => (
            <button key={qk} className="btn btn-paper" style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }} onClick={() => ask(qk)}>{qk}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') ask(); }}
            placeholder="Ask anything about the trading engine…"
            style={{ flex: 1, padding: '0.55rem', background: '#1a1a2e', color: '#fff', border: '1px solid #444', borderRadius: '6px' }}
          />
          <button className="btn btn-start" onClick={() => ask()} disabled={busy}>Ask</button>
        </div>
      </div>

      {/* Health checklist */}
      <div className="card" style={{ flex: '1 1 320px' }}>
        <div className="card-header">
          <span className="card-title">System Health</span>
          <span style={{ color: '#666', fontSize: '0.7rem' }}>{health?.checked_at || ''}</span>
        </div>
        {health ? (
          <>
            <div style={{
              padding: '0.5rem', borderRadius: '6px', marginBottom: '0.6rem', fontSize: '0.8rem', fontWeight: 600,
              background: health.can_trade ? '#00ff8815' : '#ff444415',
              color: health.can_trade ? '#00ff88' : '#ff4444',
              border: `1px solid ${health.can_trade ? '#00ff8844' : '#ff444444'}`,
            }}>{health.summary}</div>
            <div style={{ maxHeight: '440px', overflowY: 'auto' }}>
              {health.items.map((it, i) => (
                <div key={i} style={{ padding: '0.4rem 0', borderBottom: '1px solid #1a1a2e', fontSize: '0.75rem' }}>
                  <div style={{ color: LEVEL_COLOR[it.level], fontWeight: 600 }}>{LEVEL_ICON[it.level]} {it.title}</div>
                  <div style={{ color: '#999' }}>{it.detail}</div>
                  {it.solution && <div style={{ color: '#00d9ff' }}>FIX: {it.solution}</div>}
                </div>
              ))}
            </div>
          </>
        ) : <p style={{ color: '#888' }}>Loading…</p>}
      </div>
    </div>
  );
}

export default Assistant;
